import { mediaDisplayUrl } from '../../lib/resolvedMediaImage'
import { ChromeToolbarButton } from '../ChromeActions'
import { chromeIconButtonClass, chromeSelectableMediaCardClass, chromeToggleChipClass } from '../ChromeSurface'
import { ChromeTooltip } from '../ChromeTooltip'
import { IconCloseSmall, IconEyeSmall } from '../nav-icons'
import { ThemedImage } from '../ThemedImage'
import { comicPanelRoundSm } from './comicPanelTokens'

type Props = {
  angular: boolean
  imageUrl: string
  selected: boolean
  aspectClass: string
  onPreview: () => void
  onSelect: () => void
  selectLabel: string
  selectedLabel: string
  onRemove?: () => void
  onEdit?: () => void
  editLabel?: string
}

/**
 * 漫画候选成图卡片：与分镜候选、图库多选、时间线预览同一套可选媒体表面（ThemedImage + chromeSelectableMediaCard）。
 */
export function ComicCandidateMediaTile({
  angular,
  imageUrl,
  selected,
  aspectClass,
  onPreview,
  onSelect,
  selectLabel,
  selectedLabel,
  onRemove,
  onEdit,
  editLabel = '改图修字',
}: Props) {
  const displayUrl = imageUrl.trim() ? mediaDisplayUrl(imageUrl) : ''

  return (
    <li
      className={[
        'flex flex-col overflow-hidden bg-panel shadow-sm',
        chromeSelectableMediaCardClass(angular, selected),
        comicPanelRoundSm(angular),
      ].join(' ')}
    >
      <div
        className={[
          aspectClass,
          'group relative min-h-[8rem] w-full overflow-hidden bg-surface-muted',
        ].join(' ')}
      >
        <ThemedImage
          src={displayUrl}
          alt=""
          angular={angular}
          compact
          loading="lazy"
          decoding="async"
          className="absolute inset-0 z-0 h-full w-full object-cover"
          fallbackClassName="absolute inset-0 z-0 flex h-full min-h-[8rem] w-full flex-col justify-center bg-surface-muted"
        />
        {selected ? (
          <span className="pointer-events-none absolute left-2 top-2 z-[2] rounded-[var(--radius-sm)] bg-accent px-1.5 py-0.5 text-[10px] font-bold text-white">
            已定稿
          </span>
        ) : null}
        <ChromeToolbarButton
          type="button"
          className={[
            'absolute z-[3] !m-0 !h-7 !min-h-7 !w-7 !min-w-7 !gap-0 !p-0',
            onRemove && !selected ? 'bottom-2 right-2' : 'right-2 top-2',
            '!border-line/45 !bg-[color-mix(in_srgb,var(--panel)_40%,transparent)] shadow-sm backdrop-blur-[2px]',
            'hover:!border-line/60 hover:!bg-[color-mix(in_srgb,var(--panel)_58%,transparent)]',
            angular ? '!rounded-none !border-2' : '!rounded-full',
          ].join(' ')}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onPreview()
          }}
          aria-label="放大预览"
        >
          <IconEyeSmall className="h-[15px] w-[15px] shrink-0" aria-hidden />
        </ChromeToolbarButton>
        {onRemove && !selected ? (
          <ChromeTooltip
            content="删除候选图"
            angular={angular}
            side="top"
            align="end"
            className="absolute right-2 top-2 z-[3]"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onRemove()
              }}
              className={[
                chromeIconButtonClass(angular),
                '!h-8 !w-8 text-red-600 hover:border-red-400/80 hover:text-red-700',
              ].join(' ')}
              aria-label="删除候选图"
            >
              <IconCloseSmall className="h-4 w-4" aria-hidden />
            </button>
          </ChromeTooltip>
        ) : null}
      </div>
      <div className="flex gap-2 p-2">
        <button
          type="button"
          onClick={onSelect}
          className={[chromeToggleChipClass(angular, selected), 'flex-1 text-xs'].join(' ')}
        >
          {selected ? selectedLabel : selectLabel}
        </button>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className={[chromeToggleChipClass(angular, false), 'text-xs'].join(' ')}
          >
            {editLabel}
          </button>
        ) : null}
      </div>
    </li>
  )
}
